import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/groupDetails/GroupDetail.css";
import { toast } from "react-toastify";
import api from "../utils/axiosConfig";
import Navbar from "../components/common/Navbar";


import GroupDetailHeader from "../components/groupDetails/GroupDetailHeader";
import SelectionToolbar from "../components/groupDetails/SelectionToolbar";
import SelfieMissingBanner from "../components/groupDetails/SelfieMissingBanner";
import TabsControls from "../components/groupDetails/TabsControls";
import PhotosGallery from "../components/groupDetails/PhotosGallery";
import PhotoLightboxModal from "../components/groupDetails/PhotoLightboxModal";
import UploadProgressModal from "../components/groupDetails/UploadProgressModal";
import IndexingProgress from "../components/groupDetails/IndexingProgress";
import ShareGroupModal from "../components/groupDetails/ShareGroupModal";
import { usePhotoSelection } from "../hooks/groupDetail/usePhotoSelection";
import { useLightbox } from "../hooks/groupDetail/useLightbox";
import { useFaceMatch } from "../hooks/groupDetail/useFaceMatch";

import { getBrowserSafeUrl, getThumbUrl, getPreviewUrl } from "../utils/groupDetail/urlHelpers";
import { useGroupDownload } from "../hooks/groupDetail/useGroupDownload";
import { useGroupUpload } from "../hooks/groupDetail/useGroupUpload";
import { formatTime, estimateTimeLeft, getInterleavedColumns } from "../utils/groupDetail/uiHelpers";
import { scanFilesRecursively } from "../utils/groupDetail/uploadHelpers";

const GroupDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();


  const [groupData, setGroupData] = useState(null);
  const [media, setMedia] = useState([]);
  const [currentTab, setCurrentTab] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);




  // Sort state
  const [sortBy, setSortBy] = useState("latest"); // latest, oldest, name
  const [columnCount, setColumnCount] = useState(5);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isMainAdmin =
    user?.role === "admin" ||
    String(groupData?.createdBy?._id || groupData?.createdBy) === user?._id;

  const isGroupAdmin =
    isMainAdmin ||
    (groupData?.permittedUsers &&
      groupData.permittedUsers.some(
        (u) => String(u._id || u) === user?._id
      ));

  const {
    myFacePhotos,
    isMatching,
    elapsedTime,
    resetFaceMatch
  } = useFaceMatch(id, user, media);

  // Responsive column count listener for dynamic mixed grid
  useEffect(() => {
    const updateColumns = () => {
      const w = window.innerWidth;
      if (w > 1200) setColumnCount(5);
      else if (w > 850) setColumnCount(4);
      else if (w > 550) setColumnCount(3);
      else setColumnCount(2);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);


  const fetchGroup = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/groups/${id}?includeMedia=true&_t=${Date.now()}`);
      setGroupData(res.data);
      setMedia(res.data.media || []);
    } catch (error) {
      console.error("Error fetching group:", error);
      toast.error("Failed to load group details");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // Fetch group data
  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  // Set default tab
  useEffect(() => {
    if (!groupData) return;
    if (!user?.selfie && !isGroupAdmin) {
      setCurrentTab("all");
    } else {
      setCurrentTab("my");
    }
  }, [groupData, isGroupAdmin, user?.selfie]);

  const {
    isDraggingFiles,
    setIsDraggingFiles,
    handleUpload,
    uploadModalProps,
  } = useGroupUpload({
    id,
    media,
    setMedia,
    fetchGroup,
    resetFaceMatch,
  });

  const getDisplayedPhotos = useCallback(() => {
    let photos = !isGroupAdmin || currentTab === "my" ? myFacePhotos : media;

    // Sort
    if (sortBy === "latest") {
      photos = [...photos].reverse();
    } else if (sortBy === "name") {
      photos = [...photos].sort((a, b) =>
        (a.fileName || "").localeCompare(b.fileName || "")
      );
    }

    return photos;
  }, [isGroupAdmin, currentTab, myFacePhotos, media, sortBy]);

  const displayedPhotos = getDisplayedPhotos();

  const {
    selectionMode,
    setSelectionMode,
    selectedPhotos,
    setSelectedPhotos,
    togglePhotoSelection,
    selectAllPhotos,
    deselectAllPhotos,
  } = usePhotoSelection(getDisplayedPhotos);

  const {
    previewPhoto,
    currentImageIndex,
    isModalOpen,
    isHighResLoaded,
    setIsHighResLoaded,
    handleModalClose,
    handleImageClick,
    handleNextImage,
    handlePrevImage,
  } = useLightbox(displayedPhotos);

  const clearSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedPhotos(new Set());
  }, [setSelectionMode, setSelectedPhotos]);

  const {
    handleDownloadSelected,
    handleDownloadAll,
    handleDownloadCurrentImage
  } = useGroupDownload({
    groupData,
    displayedPhotos,
    selectedPhotos,
    clearSelection,
    previewPhoto,
    currentImageIndex
  });

  const handleDeleteSelected = async () => {
    if (!isMainAdmin) return;
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedPhotos.size} selected photo(s)?`)) return;

    try {
      const mediaIds = Array.from(selectedPhotos);

      await api.post("/media/delete-selected", {
        groupId: id,
        mediaIds
      });

      toast.success("Photos deleted successfully");
      clearSelection();
      // Re-fetch media to update the UI
      fetchGroup();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.message || "Failed to delete photos");
    }
  };

  const handleShowCode = () => {
    if (!groupData?.code) {
      toast.error("Group code not available");
      return;
    }
    setShowShareModal(true);
  };

  const handleBack = () => {
    if (user?.role === "admin") {
      navigate("/admin-dashboard");
    } else if (user?._id) {
      navigate("/user-dashboard");
    } else {
      navigate("/login");
    }
  };





  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading group details...</p>
      </div>
    );
  }

  if (!groupData) {
    return (
      <div className="loading-container">
        <p>Group not found</p>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <Navbar
        isAlbum={true}
        albumTitle={groupData.name}
        onBack={handleBack}
      />

      <main
        className={`group-container ${isDraggingFiles ? "drag-over-active" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!isGroupAdmin) return;
          if (!isDraggingFiles) setIsDraggingFiles(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          if (!isGroupAdmin) return;
          if (e.currentTarget.contains(e.relatedTarget)) return;
          setIsDraggingFiles(false);
        }}
        onDrop={async (e) => {
          e.preventDefault();
          if (!isGroupAdmin) return;
          setIsDraggingFiles(false);
          const items = Array.from(e.dataTransfer.items || []);
          if (items.length > 0) {
            const filesToUpload = [];
            for (const item of items) {
              const entry = item.webkitGetAsEntry();
              if (entry) {
                const scanned = await scanFilesRecursively(entry);
                filesToUpload.push(...scanned);
              }
            }
            if (filesToUpload.length > 0) {
              handleUpload(filesToUpload);
            }
          } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleUpload(e.dataTransfer.files);
          }
        }}
      >
        {isDraggingFiles && (
          <div className="global-drag-overlay">
            <div className="drag-overlay-content">
              <span className="drag-icon">📥</span>
              <h3>Drop Photos Anywhere to Upload</h3>
              <p>Adding photos directly to {groupData.name}</p>
            </div>
          </div>
        )}

        <GroupDetailHeader
          groupData={groupData}
          isMainAdmin={isMainAdmin}
          canUpload={isGroupAdmin}
          displayedPhotos={displayedPhotos}
          uploading={uploadModalProps.uploading}
          handleUpload={handleUpload}
          handleShowCode={handleShowCode}
          handleDownloadAll={handleDownloadAll}
          navigate={navigate}
          id={id}
        />

        <IndexingProgress groupId={id} canUpload={isGroupAdmin} />

        {/* Upload Progress (Old implementation removed, using Modal instead) */}

        <SelectionToolbar
          selectedPhotos={selectedPhotos}
          selectAllPhotos={selectAllPhotos}
          deselectAllPhotos={deselectAllPhotos}
          handleDownloadSelected={handleDownloadSelected}
          handleDeleteSelected={handleDeleteSelected}
          isMainAdmin={isMainAdmin}
        />

        <SelfieMissingBanner user={user} navigate={navigate} />

        <TabsControls
          isGroupAdmin={isGroupAdmin}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          myFacePhotos={myFacePhotos}
          media={media}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />

        <div className="gallery-scroll-area">
          <PhotosGallery
            currentTab={currentTab}
            isMatching={isMatching}
            canUpload={isGroupAdmin}
            elapsedTime={elapsedTime}
            formatTime={formatTime}
            estimateTimeLeft={() => estimateTimeLeft(elapsedTime)}
            displayedPhotos={displayedPhotos}
            columnCount={columnCount}
            getInterleavedColumns={getInterleavedColumns}
            selectedPhotos={selectedPhotos}
            selectionMode={selectionMode}
            setSelectionMode={setSelectionMode}
            handleImageClick={handleImageClick}
            togglePhotoSelection={togglePhotoSelection}
            getThumbUrl={getThumbUrl}
            getBrowserSafeUrl={getBrowserSafeUrl}
            handleUpload={handleUpload}
          />
        </div>

        <PhotoLightboxModal
          isModalOpen={isModalOpen}
          previewPhoto={previewPhoto}
          currentImageIndex={currentImageIndex}
          isHighResLoaded={isHighResLoaded}
          setIsHighResLoaded={setIsHighResLoaded}
          handleModalClose={handleModalClose}
          handlePrevImage={handlePrevImage}
          handleNextImage={handleNextImage}
          handleDownloadCurrentImage={handleDownloadCurrentImage}
          handleShowCode={handleShowCode}
          getDisplayedPhotos={getDisplayedPhotos}
          getThumbUrl={getThumbUrl}
          getPreviewUrl={getPreviewUrl}
          getBrowserSafeUrl={getBrowserSafeUrl}
          groupData={groupData}
          selectedPhotos={selectedPhotos}
          togglePhotoSelection={togglePhotoSelection}
        />
      </main>

      <UploadProgressModal {...uploadModalProps} />

      <ShareGroupModal
        showModal={showShareModal}
        onClose={() => setShowShareModal(false)}
        groupData={groupData}
      />
    </div>
  );
};

export default GroupDetail;